import {
  createRootRoute,
  createRoute,
  createRouter,
  type AnyRoute,
} from '@tanstack/react-router';

import type { WebAppConfig } from '@/app-config/model/types';
import { defaultProfile } from '@/app-config/profiles/default';
import { validateAppConfig } from '@/app-config/model/validate-app-config';
import { resolveShellConfig } from '@/app-config/routing/shell-registry';

import { createAuthRoutes } from '@/features/auth/routes';
import { createGradingRoutes } from '@/features/grading/routes';
import { createInboxRoutes } from '@/features/inbox/routes';
import {
  createInvitationRoute,
  createOnboardingRoutes,
} from '@/features/onboarding/routes';
import { createTenantAdminRoutes } from '@/features/platform/routes';
import { createQuestionRoutes } from '@/features/question/routes';
import { createQuizRoutes } from '@/features/quiz/routes';
import { createStudentQuizRoutes } from '@/features/student-quiz/routes';
import { createTeachingRoutes } from '@/features/teaching/routes';
import { createThreadRoutes } from '@/features/thread/routes';
import { createTimetableRoutes } from '@/features/timetable/routes';
import { createUfpRoutes } from '@/features/ufp/routes';
import { NotFoundPage } from '@/routes/not-found';

function enabledRoutes<const Routes extends readonly AnyRoute[]>(
  enabled: boolean,
  factory: () => Routes,
): Routes {
  // TanStack's Register describes the maximum route set supported by this
  // build. A profile may remove routes at runtime, but returning `Routes | []`
  // would erase every optional route from Link/params/search type inference.
  return (enabled ? factory() : []) as unknown as Routes;
}

export function createAppRouter(appConfig: WebAppConfig) {
  validateAppConfig(appConfig);
  const shells = resolveShellConfig(appConfig.shell);
  const enabledFeatures = new Set(
    appConfig.features.map(({ descriptor }) => descriptor.id),
  );
  const isEnabled = (featureId: string): boolean =>
    enabledFeatures.has(featureId);

  if (!isEnabled('app-foundation')) {
    throw new Error('Required Web Feature is disabled: app-foundation');
  }

  const foundation = appConfig.features.find(
    ({ descriptor }) => descriptor.id === 'app-foundation',
  );
  if (!foundation) throw new Error('Missing Web Feature: app-foundation');

  const screen = (routeId: string) => {
    const component = (foundation.screens as Readonly<Record<string, unknown>>)[
      routeId
    ];
    if (!component) throw new Error(`Missing foundation screen: ${routeId}`);
    return component as NonNullable<AnyRoute['options']['component']>;
  };

  const rootRoute = createRootRoute({
    component: shells.root,
    notFoundComponent: NotFoundPage,
  });
  const landingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/landing',
    component: screen('app-foundation.landing'),
  });

  const publicLayout = createRoute({
    getParentRoute: () => rootRoute,
    id: '_public',
    component: shells.public,
  });
  const appLayout = createRoute({
    getParentRoute: () => rootRoute,
    id: '_app',
    component: shells.app,
  });
  const accountLayout = createRoute({
    getParentRoute: () => appLayout,
    id: '_account',
    component: shells.account,
    notFoundComponent: NotFoundPage,
  });
  const workspaceLayout = createRoute({
    getParentRoute: () => appLayout,
    path: '/w/$tenantId',
    component: shells.workspace,
    notFoundComponent: NotFoundPage,
  });

  const invitationRoutes = enabledRoutes(
    isEnabled('onboarding'),
    () => [createInvitationRoute(rootRoute)] as const,
  );
  const authRoutes = enabledRoutes(isEnabled('auth'), () =>
    createAuthRoutes(publicLayout),
  );
  const onboardingRoutes = enabledRoutes(isEnabled('onboarding'), () =>
    createOnboardingRoutes(accountLayout),
  );
  const questionRoutes = enabledRoutes(isEnabled('question'), () =>
    createQuestionRoutes(workspaceLayout),
  );
  const threadRoutes = enabledRoutes(isEnabled('thread'), () =>
    createThreadRoutes(workspaceLayout),
  );
  const quizRoutes = enabledRoutes(isEnabled('quiz'), () =>
    createQuizRoutes(workspaceLayout),
  );
  const studentQuizRoutes = enabledRoutes(isEnabled('student-quiz'), () =>
    createStudentQuizRoutes(workspaceLayout),
  );
  const gradingRoutes = enabledRoutes(isEnabled('grading'), () =>
    createGradingRoutes(workspaceLayout),
  );
  const tenantAdminRoutes = enabledRoutes(isEnabled('tenant-admin'), () =>
    createTenantAdminRoutes(workspaceLayout),
  );
  const ufpRoutes = enabledRoutes(isEnabled('ufp'), () =>
    createUfpRoutes(workspaceLayout),
  );
  const teachingRoutes = enabledRoutes(isEnabled('teaching'), () =>
    createTeachingRoutes(workspaceLayout),
  );
  const timetableRoutes = enabledRoutes(isEnabled('timetable'), () =>
    createTimetableRoutes(workspaceLayout),
  );
  const inboxRoutes = enabledRoutes(isEnabled('inbox'), () =>
    createInboxRoutes(workspaceLayout),
  );

  const accountDashboardRoute = createRoute({
    getParentRoute: () => accountLayout,
    path: '/',
    component: screen('app-foundation.account-home'),
  });
  const workspacesRoute = createRoute({
    getParentRoute: () => accountLayout,
    path: '/workspaces',
    component: screen('app-foundation.workspaces'),
  });
  const forbiddenRoute = createRoute({
    getParentRoute: () => accountLayout,
    path: '/403',
    component: screen('app-foundation.account-forbidden'),
  });
  const workspaceIndexRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/',
    component: screen('app-foundation.workspace-home'),
  });
  const workspaceForbiddenRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/403',
    component: screen('app-foundation.workspace-forbidden'),
  });
  const settingsRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/settings',
    component: screen('app-foundation.settings'),
  });
  const studentProfileRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/student-profile',
    component: screen('app-foundation.student-profile'),
  });
  const studentProfileDetailRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/student-profiles/$studentProfileId',
    component: screen('app-foundation.student-profile-detail'),
  });
  const studentProfileOnboardingRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/student-profile/onboarding',
    component: screen('app-foundation.student-profile-onboarding'),
  });
  const studentProfileChangeRequestRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/student-profile-change-requests/$requestId',
    component: screen('app-foundation.student-profile-change-request'),
  });
  const studentProfileChangeRequestsRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/student-profile-change-requests',
    component: screen('app-foundation.student-profile-change-requests'),
  });
  const materialsRoute = createRoute({
    getParentRoute: () => workspaceLayout,
    path: '/materials',
    component: screen('app-foundation.materials'),
  });

  const routeTree = rootRoute.addChildren([
    landingRoute,
    ...invitationRoutes,
    publicLayout.addChildren([...authRoutes]),
    appLayout.addChildren([
      accountLayout.addChildren([
        accountDashboardRoute,
        workspacesRoute,
        ...onboardingRoutes,
        forbiddenRoute,
      ]),
      workspaceLayout.addChildren([
        workspaceIndexRoute,
        workspaceForbiddenRoute,
        ...threadRoutes,
        settingsRoute,
        studentProfileRoute,
        studentProfileDetailRoute,
        studentProfileOnboardingRoute,
        studentProfileChangeRequestRoute,
        studentProfileChangeRequestsRoute,
        ...questionRoutes,
        ...quizRoutes,
        ...studentQuizRoutes,
        ...gradingRoutes,
        ...tenantAdminRoutes,
        ...ufpRoutes,
        ...teachingRoutes,
        ...timetableRoutes,
        ...inboxRoutes,
        materialsRoute,
      ]),
    ]),
  ]);

  /**
   * defaultPreload: 'intent'
   * 用户对链接产生导航意图时（例如悬停或获得键盘焦点），提前准备目标路由。
   * 这样可以减少用户实际点击后的等待，同时避免应用启动时加载所有页面。
   * 预加载只是一项性能优化，不会绕过 AuthGuard 或 WorkspaceShell 的访问检查。
   */
  return createRouter({ routeTree, defaultPreload: 'intent' });
}

export const router = createAppRouter(defaultProfile);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
